import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Box, makeStyles } from '@material-ui/core';
import { Editor } from 'react-draft-wysiwyg';
import { useSelector } from 'react-redux';
import { BED_STATUS_CUBICLE_COLOR, BED_STATUS_REMARK_COLOR } from '../../../comm/constant';
import { classNames, onKeyUpEnterOrSpace } from '../../../utils';
import useCheckLogin from '../../../utils/useCheckLogin';
import { CUBICLE_VIEW2 } from '../../../redux/constant';
import EditMemo from './Popups/EditMemo';
import UpdateMemo from './Popups/UpdateMemo';

const useStyles = makeStyles(() => ({
  root: ({ color }) => ({
    backgroundColor: BED_STATUS_CUBICLE_COLOR[color] ?? '#fff',
  }),
  name: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    color: '#7d86a9',
    fontSize: '1.25rem',
    width: '80%',
    textAlign: 'center',
  },
  title: {
    fontFamily: 'NotoSans-Bold',
    fontSize: '0.875rem',
    color: '#2e4469',
    lineHeight: '1.05rem',
    '&:hover': {
      textDecoration: 'underline',
    },
  },
  status: {
    backgroundColor: '#979bff',
    padding: '0.125rem 0.25rem',
    borderRadius: '2px',
  },
  statusContent: {
    color: '#fff',
    fontSize: '0.75rem',
    fontFamily: 'NotoSans-Medium',
  },
  clickStyle: {
    '&:hover': {
      cursor: 'default',
    },
  },
  tipBar: ({ remarkColor }) => ({
    backgroundColor: BED_STATUS_REMARK_COLOR[remarkColor]?.bgColor ?? '#fff',
    borderRadius: '4px',
    border: 'solid 1px #e4e7eb',
    padding: '0.25rem 0.5rem',
    position: 'absolute',
    height: '100%',
    top: 0,
    left: 0,
    right: 0,
    overflow: 'hidden',
    '&::before': {
      position: 'absolute',
      top: '0',
      left: '0',
      content: '""',
      display: 'block',
      width: '0.25rem',
      height: '100%',
      backgroundColor: BED_STATUS_REMARK_COLOR[remarkColor]?.leftColor ?? '#fff',
      zIndex: '9',
    },
  }),
  tips: ({ remarkColor }) => ({
    fontFamily: 'NotoSans-Medium',
    color: BED_STATUS_REMARK_COLOR[remarkColor]?.color ?? '#2e4469',
    fontSize: '0.75rem',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    WebkitBoxOrient: 'vertical',
    display: '-webkit-box',
    whiteSpace: 'pre-wrap',
    '& .public-DraftStyleDefault-block': {
      margin: 'unset',
    },
  }),
  wrapper: {
    // height: '100%',
  },
  editor: {
    overflow: 'inherit',
  },
}));

const Memo = ({
  handleDeleteCubicle,
  handleEditCubicle,
  handleCopyCubicle,
  handleUpdateCubicle,
  edit,
  setPopup,
  readOnly = false,
  ...data
}) => {
  const classes = useStyles({ color: data.color, remarkColor: data.remarkColor });
  const remarkRef = useRef();
  const remarkContentRef = useRef();
  const [editPopup, setEditPopup] = useState(false);
  const { isCubicleV2 } = useSelector((state) => state.bedStatusView);
  const checkLogin = useCheckLogin();

  useEffect(() => {
    const lineHeight = parseFloat(getComputedStyle(remarkContentRef.current).lineHeight);
    const lines = Math.floor(remarkRef.current.clientHeight / lineHeight) - 1;

    remarkContentRef.current.style.webkitLineClamp = lines;
  }, [remarkRef, data.bedArrangement]);

  const EditPopup = useMemo(() => {
    return edit ? EditMemo : UpdateMemo;
  }, [edit]);

  const handleOpenEditPopup = useCallback(() => {
    if (checkLogin() && !readOnly) {
      setEditPopup(true);
    }
  }, [checkLogin, readOnly]);

  return (
    <>
      <Box
        className={classes.root}
        style={data.style}
        position="relative"
        display="inline-flex"
        minWidth={isCubicleV2 ? '9.964rem' : '11rem'}
        minHeight="9.3rem"
        border={data.borderLine === 'show' ? 'solid 1px #AAAAAA' : 'unset'}
        borderRadius="8px"
        p={isCubicleV2 ? '0.357rem' : '0.875rem 0.813rem 0.875rem 0.688rem'}
      >
        <Box
          display="flex"
          alignItems="flex-start"
          justifyContent="space-between"
          position="absolute"
          width="calc(100% - 0.813rem - 0.688rem)"
          top="0.875rem"
        >
          <Box display="flex" alignItems="center">
            <div
              className={classNames(classes.title, classes.clickStyle)}
              onClick={handleOpenEditPopup}
              tabIndex={0}
              onKeyUp={(e) => onKeyUpEnterOrSpace(e, handleOpenEditPopup)}
            >
              {data.name}
            </div>
          </Box>
          {data.status && (
            <Box display="flex" alignItems="center">
              <div
                className={classNames(classes.status, classes.clickStyle)}
                onClick={handleOpenEditPopup}
                tabIndex={0}
                onKeyUp={(e) => onKeyUpEnterOrSpace(e, handleOpenEditPopup)}
              >
                <div className={classes.statusContent}>{data.status}</div>
              </div>
            </Box>
          )}
        </Box>
        <Box position="absolute" top="2.36rem" right="0.813rem" left="0.688rem" bottom="0.875rem" marginTop="0.5rem">
          <Box position="relative" height="100%">
            <div
              className={classes.tipBar}
              ref={remarkRef}
              onClick={handleOpenEditPopup}
              tabIndex={0}
              onKeyUp={(e) => onKeyUpEnterOrSpace(e, handleOpenEditPopup)}
            >
              <div className={classes.tips} ref={remarkContentRef}>
                <Editor
                  editorState={data.remarks}
                  wrapperClassName={classes.wrapper}
                  editorClassName={classes.editor}
                  readOnly // 设置为只读模式，用于显示内容
                  toolbarHidden // 隐藏工具栏
                />
              </div>
            </div>
          </Box>
        </Box>
        {/* 撑开size */}
        <Box display="flex" flexDirection="column">
          <Box height="4.5rem" width="100%" />
          <Box
            display="grid"
            gridTemplateRows={`repeat(${data.bedArrangement.row}, 1fr)`}
            gridTemplateColumns={`repeat(${data.bedArrangement.column}, 1fr)`}
            gridGap="0.2rem"
          >
            {data.beds.map((item) => (
              <Box
                key={item.key}
                display="flex"
                alignItems="center"
                justifyContent="center"
                width="5rem"
                height="4.688rem"
                pt="1.563rem"
              />
            ))}
          </Box>
        </Box>
      </Box>
      {editPopup && (
        <EditPopup
          edit={data.edit}
          data={data}
          handleClose={() => setEditPopup(false)}
          handleDeleteCubicle={handleDeleteCubicle}
          handleEditCubicle={handleEditCubicle}
          handleCopyCubicle={handleCopyCubicle}
          handleUpdateCubicle={handleUpdateCubicle}
          setPopup={setPopup}
        />
      )}
    </>
  );
};

export default Memo;
